import { FvAndPpChartsTableData, GroupedPortfolioExcelWrapper } from
  'src/app/domain/organization/components/fair-value-and-pp-charts/data-behind-fv-and-pp-charts/data-behind-fv-and-pp-charts.component';
import { Rows } from '../../interfaces';
import { PartialCell, formats, getCell, withCellStyles } from '../../utils';
import { boldLightGrayBgLeft, left, leftBold } from 'src/app/util/excelStyles';

export const getPreparedData = (wrapper: GroupedPortfolioExcelWrapper, headers: string[]): Rows => {
  const newData: Rows = [];
  newData.push(headers);
  if (wrapper.isOnlyCompanies) {
    const onlyCompaniesRows = getOnlyCompaniesRow(wrapper.rawData.body[0].drilldown, wrapper.categoryName, headers);
    newData.push(...onlyCompaniesRows);
  } else {
    wrapper.rawData.body.forEach((groupedBy) => {
      totalRowIndex += 1;
      totalRowIndex += groupedBy.drilldown.length;
    });
    wrapper.rawData.body.forEach((groupedBy) => newData.push(...getMainRows(groupedBy, wrapper.categoryName, headers)));
  }
  const totalRow = getTotalRow(headers, wrapper.categoryName, wrapper.isOnlyCompanies);
  newData.push(...totalRow);
  resetData();
  return newData;
};

export const getMainRows = (groupedBy, categoryName: string, headers: string[]): Rows => {
  const rows: Rows = [];
  const categoryRowMappedToHeaders = getCategoryMapping(groupedBy, categoryName);
  for (const mapped of categoryRowMappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], leftBold));
    }
    rows.push(row);
  }
  const internalsRowsMappedTpHeaders = groupedBy.drilldown.map((dr) => getInternalMapping(dr, categoryName));
  for (const mapped of internalsRowsMappedTpHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], left));
    }
    rows.push(row);
  }
  return rows;
};


export const getCategoryMapping = (groupedBy, categoryName: string): Category[] => {
  const len = groupedBy.drilldown.length;
  const body: Category = {
    Investment: getCell({
      f: `SUM(B${currentRowIndex + 1}:B${currentRowIndex + len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: `SUM(C${currentRowIndex + 1}:C${currentRowIndex + len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)': getCell({
      f: `C${currentRowIndex}-B${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR(C${currentRowIndex}/B${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    null: null,
    '% of Cost': getCell({
      f: `IFERROR(B${currentRowIndex}/B${totalRowIndex},"")`,
      z: formats.percents,
      t: 'n',
    }),
    '% of FV': getCell({
      f: `IFERROR(C${currentRowIndex}/C${totalRowIndex},"")`,
      z: formats.percents,
      t: 'n',
    }),
  };
  body[categoryName] = groupedBy.type.name;
  categoryIndexArrayForTotal.push(currentRowIndex);
  currentRowIndex++;
  return [body];
};

export const getInternalMapping = (dr: FvAndPpChartsTableData, categoryName: string): Category => {
  const body = {
    Investment: getCell({
      v: dr.cost,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      v: dr.fairValue,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)': getCell({
      f: `C${currentRowIndex}-B${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR(C${currentRowIndex}/B${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    '% of Cost': getCell({
      f: `IFERROR(B${currentRowIndex}/B${totalRowIndex}, "")`,
      z: formats.percents,
      t: 'n',
    }),
    '% of FV': getCell({
      f: `IFERROR(C${currentRowIndex}/C${totalRowIndex}, "")`,
      z: formats.percents,
      t: 'n',
    }),
  };
  body[categoryName] = dr.companyData.name;
  currentRowIndex++;
  return body;
};

const getOnlyCompaniesRow = (drillDown: FvAndPpChartsTableData[], categoryName: string, headers: string[]): Rows => {
  const rows: Rows = [];
  const internalRowsMapped = drillDown.map((dr) => getInternalMapping(dr, categoryName));
  for (const mapped of internalRowsMapped) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], left));
    }
    rows.push(row);
  }
  return rows;
};

export const getTotalRow = (headers: string[], categoryName: string, isOnlyCompanies: boolean): Rows => {
  const rows: Rows = [];
  const totalsMappedToHeaders = getTotalMapping(categoryName, isOnlyCompanies);
  for (const mapped of totalsMappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

export const getTotalMapping = (categoryName: string, isOnlyCompanies: boolean): Category[] => {
  const body = {
    Investment: getCell({
      f: isOnlyCompanies ? generateSumFormulaForTotalsForCompaniesOnly('B') : generateSumFormulaForTotals('B'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: isOnlyCompanies ? generateSumFormulaForTotalsForCompaniesOnly('C') : generateSumFormulaForTotals('C'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)': getCell({
      f: `C${currentRowIndex}-B${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR(C${currentRowIndex}/B${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    '% of Cost': getCell({
      v: 1,
      z: formats.percents,
      t: 'n',
    }),
    '% of FV': getCell({
      v: 1,
      z: formats.percents,
      t: 'n',
    }),
  };
  body[categoryName] = 'Total';
  currentRowIndex++;
  return [body];
};

const resetData = (): void => {
  currentRowIndex = 2;
  categoryIndexArrayForTotal = [];
  totalRowIndex = 2;
};

const generateSumFormulaForTotals = (letter: string): string => {
  const columnReference = letter;
  return `SUM(${categoryIndexArrayForTotal.map((index) => `${columnReference}${index}`).join(',')})`;
};

const generateSumFormulaForTotalsForCompaniesOnly = (letter: string): string => `SUM(${letter}2:${letter}${currentRowIndex - 1})`;
// data start from row number 2
let currentRowIndex = 2;
// need to set index row for sub-total SUM formula implementation
let categoryIndexArrayForTotal = [];
let totalRowIndex = 2;

interface Category {
  'Investment': PartialCell;
  'Fair value': PartialCell;
  'Gain/(loss)': PartialCell;
  'MOIC': PartialCell;
  '% of Cost': PartialCell;
  '% of FV': PartialCell;
  null?: PartialCell;
}
